import {
  availableFileWidths,
  fileSrcSet,
  fileWidthFor,
  withFileWidth,
  type FileProxySource
} from '@Core/lib/tasks/utils/file';
import type React from 'react';
import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';

export type NativeImageProps = React.ComponentPropsWithRef<'img'>;

export type ImagePriorityToken = symbol;

/**
 * The single automatic `fetchpriority="high"` grant of one page runtime. The
 * page, its layout, and its shared components share it. A second automatic
 * high priority would only compete with the first. A claim with a larger
 * visible area takes the grant, so the likely largest paint holds it rather
 * than the first small image in document order.
 */
export type ImagePriorityGrant = {
  holder: () => ImagePriorityToken | null;
  claim: (token: ImagePriorityToken, visibleArea: number) => boolean;
  release: (token: ImagePriorityToken) => void;
  subscribe: (listener: () => void) => () => void;
};

/** The authorized file metadata that delivery reads. `FileData` satisfies it. */
export type FileImageMetadata = {
  width: number | null;
  height: number | null;
  sizes?: readonly number[];
};

type Box = {
  width: number;
  height: number;
  top: number;
  bottom: number;
};

type SourceAttempt = {
  src: string | undefined;
  box: Box | null;
  inViewport: boolean;
  inPicture: boolean;
  failures: number;
};

/**
 * How the current source renders.
 *
 * - `native`: delivery does not manage the source; author props pass through.
 * - `measuring`: the first commit, without a source, so the box can be measured.
 * - `waiting`: a zero-width box; the source loads when the box becomes positive.
 * - `responsive`: candidates from the authorized widths and a measured `sizes`.
 * - `unsized`: one proxy source, because the file has no available widths.
 * - `explicit`: the author's own `?w=` source.
 * - `recovering`: a transformed request failed; the proxy source without `w`.
 * - `original`: no metadata for the file; the authored source as written.
 */
type SourceMode =
  | 'native'
  | 'measuring'
  | 'waiting'
  | 'responsive'
  | 'unsized'
  | 'explicit'
  | 'recovering'
  | 'original';

export type FileImageOptions = {
  /** The loading value for a source that delivery does not measure. */
  defaultLoading?: 'lazy';
  /** Called once the current source has failed and no recovery remains. */
  onTerminalError?: () => void;
};

export type FileImageDelivery = FileImageOptions & {
  /** The recognized workspace file source, or null to pass the author props through. */
  source: FileProxySource | null;
  /** Authorized metadata for `source`, or undefined when none is known. */
  metadata: FileImageMetadata | undefined;
  /** The grant that automatic priority competes for, or null for no automatic priority. */
  priority?: ImagePriorityGrant | null;
  /** True when every managed image must load eagerly, as a printed document requires. */
  eagerLoading?: boolean;
};

const noSubscribe = () => () => {};
const noHolder = () => null;

function freshAttempt(src: string | undefined): SourceAttempt {
  return { src, box: null, inViewport: false, inPicture: false, failures: 0 };
}

/**
 * The element box, widened when `object-fit: cover` paints beyond its edges.
 * Keep the full box width for `contain`: reducing `sizes` to the painted
 * width also reduces an auto-width image's intrinsic size, creating a resize loop.
 */
function measureBox(element: HTMLElement, aspectRatio: number | null): Box {
  const rect = element.getBoundingClientRect();
  let width = rect.width;

  if (aspectRatio !== null && rect.width > 0 && rect.height > 0) {
    const fit = window.getComputedStyle(element).objectFit;

    if (fit === 'cover') {
      width = Math.max(width, rect.height * aspectRatio);
    }
  }

  return {
    width: Math.ceil(width),
    height: Math.ceil(rect.height),
    top: rect.top,
    bottom: rect.bottom
  };
}

function aspectRatioOf(metadata: FileImageMetadata | undefined): number | null {
  const width = metadata?.width ?? 0;
  const height = metadata?.height ?? 0;

  return width > 0 && height > 0 ? width / height : null;
}

function isInViewport(box: Box): boolean {
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  return box.width > 0 && box.height > 0 && box.bottom > 0 && box.top < viewportHeight;
}

function visibleArea(box: Box): number {
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const visibleHeight = Math.min(box.bottom, viewportHeight) - Math.max(box.top, 0);

  return isInViewport(box) ? box.width * Math.max(visibleHeight, 0) : 0;
}

function canObserveResize(): boolean {
  return typeof ResizeObserver !== 'undefined';
}

function sourceModeFor(
  managed: boolean,
  hasWidth: boolean,
  attempt: SourceAttempt,
  widths: readonly number[] | null
): SourceMode {
  if (!managed || attempt.inPicture) {
    return 'native';
  }

  if (attempt.box === null) {
    return 'measuring';
  }

  if (hasWidth) {
    return attempt.failures > 0 ? 'recovering' : 'explicit';
  }

  if (widths === null) {
    return 'original';
  }

  if (widths.length === 0) {
    return 'unsized';
  }

  if (attempt.failures > 0) {
    return 'recovering';
  }

  if (attempt.box.width === 0) {
    return canObserveResize() ? 'waiting' : 'unsized';
  }

  return 'responsive';
}

function isRetryable(mode: SourceMode): boolean {
  return mode === 'responsive' || mode === 'explicit';
}

function isMeasuredDelivery(mode: SourceMode): boolean {
  return (
    mode === 'responsive' || mode === 'unsized' || mode === 'explicit' || mode === 'recovering'
  );
}

function numericDimension(value: number | string | undefined): number | null {
  const parsed =
    typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;

  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Dimension attributes that reserve the file's intrinsic ratio. An authored
 * dimension wins. With one authored numeric dimension, the other follows the
 * file's ratio, so the authored size does not distort.
 */
function intrinsicDimensions(
  metadata: FileImageMetadata | undefined,
  deliveredWidth: number | null,
  width: number | string | undefined,
  height: number | string | undefined
): { width: number | string | undefined; height: number | string | undefined } {
  const fileWidth = metadata?.width ?? 0;
  const fileHeight = metadata?.height ?? 0;
  const intrinsicWidth = deliveredWidth ?? fileWidth;
  const intrinsicHeight = fileWidth > 0 ? Math.round((intrinsicWidth * fileHeight) / fileWidth) : 0;

  if (
    intrinsicWidth <= 0 ||
    intrinsicHeight <= 0 ||
    (width !== undefined && height !== undefined)
  ) {
    return { width, height };
  }

  if (width === undefined && height === undefined) {
    return { width: intrinsicWidth, height: intrinsicHeight };
  }

  const authoredWidth = numericDimension(width);
  const authoredHeight = numericDimension(height);

  if (authoredWidth !== null) {
    return { width, height: Math.round((authoredWidth * intrinsicHeight) / intrinsicWidth) };
  }

  if (authoredHeight !== null) {
    return { width: Math.round((authoredHeight * intrinsicWidth) / intrinsicHeight), height };
  }

  return { width, height };
}

function assignRef(
  ref: React.Ref<HTMLImageElement> | undefined,
  element: HTMLImageElement | null
): void | (() => void) {
  if (typeof ref === 'function') {
    return ref(element);
  }

  if (ref) {
    ref.current = element;
  }
}

function useAutomaticPriority(
  grant: ImagePriorityGrant | null,
  token: symbol,
  eligible: boolean,
  area: number
): boolean {
  const holder = useSyncExternalStore(
    grant?.subscribe ?? noSubscribe,
    grant?.holder ?? noHolder,
    grant?.holder ?? noHolder
  );

  useLayoutEffect(() => {
    if (grant === null) {
      return;
    }

    if (!eligible) {
      grant.release(token);
      return;
    }

    grant.claim(token, area);
    // Every eligible image offers its area on each change, so a release hands
    // the grant to the largest remaining image, not the first listener.
    const unsubscribe = grant.subscribe(() => {
      grant.claim(token, area);
    });

    return () => {
      unsubscribe();
      grant.release(token);
    };
  }, [grant, token, eligible, area]);

  return eligible && holder === token;
}

/**
 * The shared native-image delivery behind the page runtime's file images and
 * platform file previews. It returns the props for one `<img>` element. The
 * caller recognizes the source and supplies its authorized metadata. A
 * recognized source commits once without a source, is measured, and then
 * commits its authorized candidates and measured `sizes` together.
 */
export function useFileImageDelivery(
  props: NativeImageProps,
  {
    source,
    metadata,
    priority = null,
    eagerLoading = false,
    defaultLoading,
    onTerminalError
  }: FileImageDelivery
): NativeImageProps {
  const { ref, src, srcSet, sizes, loading, fetchPriority, width, height, onError, ...rest } =
    props;
  const managed = source !== null && typeof src === 'string' && srcSet === undefined;
  const widths = metadata ? availableFileWidths(metadata.sizes ?? []) : null;
  const aspectRatio = aspectRatioOf(metadata);
  const elementRef = useRef<HTMLImageElement | null>(null);
  const [token] = useState(() => Symbol('file-image'));
  const [storedAttempt, setAttempt] = useState(() => freshAttempt(src));
  let attempt = storedAttempt;

  if (storedAttempt.src !== src) {
    attempt = freshAttempt(src);
    setAttempt(attempt);
  }

  const mode = sourceModeFor(managed, source?.hasWidth ?? false, attempt, widths);
  const grant = priority;
  const measuredDelivery = isMeasuredDelivery(mode);
  const rendersNoSource = mode === 'measuring' || mode === 'waiting';
  let effectiveLoading = loading ?? defaultLoading;

  if (measuredDelivery && loading === undefined) {
    effectiveLoading = eagerLoading || attempt.inViewport ? 'eager' : 'lazy';
  } else if (rendersNoSource) {
    effectiveLoading = loading;
  } else if (mode === 'original' && loading === undefined && eagerLoading) {
    effectiveLoading = 'eager';
  }

  const hasAutomaticPriority = useAutomaticPriority(
    grant,
    token,
    measuredDelivery &&
      fetchPriority === undefined &&
      effectiveLoading === 'eager' &&
      attempt.inViewport,
    attempt.box === null ? 0 : visibleArea(attempt.box)
  );
  const attemptSrc = attempt.src;
  const measured = attempt.box !== null;
  const needsMeasure = managed && (!measured || mode === 'waiting');

  useLayoutEffect(() => {
    const element = elementRef.current;

    if (!needsMeasure || element === null) {
      return;
    }

    const inPicture = element.parentElement?.tagName === 'PICTURE';
    const box = measureBox(element, aspectRatio);
    const inViewport = isInViewport(box);

    // The measured box decides the first source, so it must be known before the
    // commit that sets one.
    setAttempt((current) => {
      if (current.src !== attemptSrc) {
        return current;
      }

      if (current.box === null) {
        return { ...current, box, inViewport, inPicture };
      }

      return current.box.width === box.width ? current : { ...current, box, inViewport };
    });

    // Claim before the source commit, so the first request carries the priority.
    // This predicts eligibility before the mode is known; useAutomaticPriority
    // releases the claim when the resolved mode is not eligible.
    if (
      grant !== null &&
      !inPicture &&
      inViewport &&
      fetchPriority === undefined &&
      loading !== 'lazy'
    ) {
      grant.claim(token, visibleArea(box));
    }
  }, [needsMeasure, attemptSrc, aspectRatio, grant, token, fetchPriority, loading]);

  // Unsized and metadata-less images are observed too: later metadata switches
  // them to responsive delivery, which needs the current box, not a stale one.
  const observes = managed && measured && !attempt.inPicture && !source.hasWidth;

  useEffect(() => {
    const element = elementRef.current;

    if (!observes || element === null || !canObserveResize()) {
      return;
    }

    const observer = new ResizeObserver(() => {
      const box = measureBox(element, aspectRatio);

      setAttempt((current) => {
        if (current.src !== attemptSrc || current.box === null || current.box.width === box.width) {
          return current;
        }

        const revealed = current.box.width === 0 && box.width > 0;

        return {
          ...current,
          box,
          inViewport: revealed ? isInViewport(box) : current.inViewport
        };
      });
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, [observes, attemptSrc, aspectRatio]);

  // An explicit width request is delivered at the server's covering width,
  // so its natural size is that width, not the file's.
  const deliveredWidth =
    mode === 'explicit' && source?.width != null && widths !== null
      ? fileWidthFor(widths, source.width)
      : null;
  const dimensions =
    mode === 'native'
      ? { width, height }
      : intrinsicDimensions(metadata, deliveredWidth, width, height);
  let resolvedSrc: string | undefined = src;
  let resolvedSrcSet: string | undefined = srcSet;
  let resolvedSizes: string | undefined = sizes;

  if (rendersNoSource) {
    resolvedSrc = undefined;
  } else if (mode === 'responsive' && typeof src === 'string' && widths !== null && attempt.box) {
    resolvedSrc = withFileWidth(src, fileWidthFor(widths, attempt.box.width));
    resolvedSrcSet = fileSrcSet(src, widths);
    resolvedSizes = sizes ?? `${attempt.box.width}px`;
  } else if (mode === 'recovering' && typeof src === 'string') {
    resolvedSrc = withFileWidth(src, null);
    // Chrome keeps the failed width candidate's density after `srcset` is
    // removed, which scales the original; a `1x` candidate resets it.
    resolvedSrcSet = `${resolvedSrc} 1x`;
  }

  const handleError = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    onError?.(event);

    if (isRetryable(mode) && attempt.failures === 0) {
      setAttempt((current) =>
        current.src === attemptSrc && current.failures === 0 ? { ...current, failures: 1 } : current
      );
      return;
    }

    setAttempt((current) =>
      current.src === attemptSrc ? { ...current, failures: current.failures + 1 } : current
    );
    onTerminalError?.();
  };

  const composedRef = (element: HTMLImageElement | null) => {
    elementRef.current = element;
    const cleanup = assignRef(ref, element);

    return () => {
      elementRef.current = null;

      if (typeof cleanup === 'function') {
        cleanup();
      } else {
        assignRef(ref, null);
      }
    };
  };

  // React sets updated attributes in prop order, so `loading`, `sizes`, and
  // `srcSet` must precede `src` for the first request to use them.
  return {
    ...rest,
    ref: composedRef,
    width: dimensions.width,
    height: dimensions.height,
    loading: effectiveLoading,
    fetchPriority: hasAutomaticPriority ? 'high' : fetchPriority,
    sizes: resolvedSizes,
    srcSet: resolvedSrcSet,
    src: resolvedSrc,
    onError: handleError
  };
}
